import React, { useEffect, useMemo, useState } from "react";
import OfficeHoursService from "../office/officeHoursService";
import TooltipInput from "../../NewUPT/ToolTip/TooltipInput";
import "../../NewUPT/registration/searchPopupDesign.css";

const RegisterOfficeSchedule = () => {
  const [officeId, setOfficeId] = useState("");
  const [officeName, setOfficeName] = useState("");

  const [selectedDays, setSelectedDays] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [numberOfWeeks, setNumberOfWeeks] = useState("1");

  const [openHour, setOpenHour] = useState("");
  const [closeHour, setCloseHour] = useState("");
  const [increments, setIncrements] = useState("1");

  const officeService = useMemo(() => new OfficeHoursService(), []);

  const days = [
    { label: "Monday", value: "MONDAY" },
    { label: "Tuesday", value: "TUESDAY" },
    { label: "Wednesday", value: "WEDNESDAY" },
    { label: "Thursday", value: "THURSDAY" },
    { label: "Friday", value: "FRIDAY" },
    { label: "Saturday", value: "SATURDAY" },
    { label: "Sunday", value: "SUNDAY" },
  ];

  useEffect(() => {
    const receivePopupMessage = (event) => {
      const { inputTarget, value, displayName } = event.data || {};

      if (inputTarget === "officeId") {
        setOfficeId(value);
        setOfficeName(displayName || "");
      }
    };

    window.addEventListener("message", receivePopupMessage);

    return () => {
      window.removeEventListener("message", receivePopupMessage);
    };
  }, []);

  const openSearchPopup = () => {
    const popupUrl = `/search-popup?type=Office&inputTarget=officeId&url=${encodeURIComponent(
      "/api/office/search"
    )}`;

    window.open(popupUrl, "_blank", "width=800,height=600");
  };

  const toggleDay = (dayValue) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayValue)) {
        return prev.filter((day) => day !== dayValue);
      }

      return [...prev, dayValue];
    });
  };

  const getSelectedDayLabels = () => {
    return days
      .filter((day) => selectedDays.includes(day.value))
      .map((day) => day.label)
      .join(", ");
  };

  const calculateEstimatedRows = () => {
    const weeks = parseInt(numberOfWeeks, 10);

    if (!weeks || weeks <= 0 || selectedDays.length === 0) {
      return 0;
    }

    return selectedDays.length * weeks;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parsedOfficeId = parseInt(officeId, 10);
    const parsedWeeks = parseInt(numberOfWeeks, 10);
    const parsedIncrements = parseInt(increments, 10);

    if (!parsedOfficeId) {
      alert("Please select an office.");
      return;
    }

    if (selectedDays.length === 0) {
      alert("Please select at least one day of the week.");
      return;
    }

    if (!startDate) {
      alert("Please select a start date.");
      return;
    }

    if (!parsedWeeks || parsedWeeks <= 0) {
      alert("Please enter a valid number of weeks.");
      return;
    }

    if (!openHour || !closeHour) {
      alert("Please enter both open hour and close hour.");
      return;
    }

    if (!parsedIncrements || parsedIncrements <= 0) {
      alert("Appointments per 30 minutes must be greater than 0.");
      return;
    }

    const confirmMessage =
      `You are about to register office hours for:\n\n` +
      `Office: ${officeName}\n` +
      `Days: ${getSelectedDayLabels()}\n` +
      `Start Date: ${startDate}\n` +
      `Number of Weeks: ${parsedWeeks}\n` +
      `Time: ${openHour} - ${closeHour}\n` +
      `Appointments per 30 mins: ${parsedIncrements}\n\n` +
      `This will create about ${calculateEstimatedRows()} schedule rows.\n\n` +
      `Do you want to continue?`;

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) {
      return;
    }

    const data = {
      officeId: parsedOfficeId,
      daysOfWeek: selectedDays,
      startDate,
      numberOfWeeks: parsedWeeks,
      openHour,
      closeHour,
      incraments: parsedIncrements,
    };

    try {
      const response = await officeService.saveBulkOfficeHours(data);

      alert(`✅ Office schedules registered successfully.\nRows created: ${response.length}`);

      setSelectedDays([]);
      setStartDate("");
      setNumberOfWeeks("1");
      setOpenHour("");
      setCloseHour("");
      setIncrements("1");
    } catch (error) {
      alert("❌ Failed to register office hours:\n" + error.message);
    }
  };

  return (
    <div className="form-wrapper">
      <form id="officeScheduleRegistrationForm" onSubmit={handleSubmit}>
        <h2>Register Office Schedule</h2>

        <div className="input-container">
          <label htmlFor="officeSearch">Office</label>
          <input
            type="text"
            id="officeSearch"
            value={officeName}
            placeholder="Click to select office"
            readOnly
            className="search-display"
            onClick={openSearchPopup}
          />
          <input type="hidden" id="officeId" value={officeId} readOnly />
        </div>

        <div className="input-container">
          <label>Select Days</label>

          <div className="checkbox-grid">
            {days.map((day) => (
              <label key={day.value} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={selectedDays.includes(day.value)}
                  onChange={() => toggleDay(day.value)}
                />
                {day.label}
              </label>
            ))}
          </div>
        </div>

        <TooltipInput
          type="date"
          id="startDate"
          placeholder="Start Date"
          tooltip="Choose the first date to start generating office schedules."
          value={startDate}
          onChange={setStartDate}
        />

        <TooltipInput
          type="number"
          id="numberOfWeeks"
          placeholder="Number of weeks"
          tooltip="How many weeks should this schedule repeat?"
          value={numberOfWeeks}
          onChange={setNumberOfWeeks}
        />

        <TooltipInput
          type="time"
          id="openHour"
          placeholder="Open Hour"
          tooltip="Opening time for the office."
          value={openHour}
          onChange={setOpenHour}
        />

        <TooltipInput
          type="time"
          id="closeHour"
          placeholder="Close Hour"
          tooltip="Closing time for the office."
          value={closeHour}
          onChange={setCloseHour}
        />

        <TooltipInput
          type="number"
          id="increments"
          placeholder="Appointments per 30 mins"
          tooltip="How many appointments are allowed every 30 minutes."
          value={increments}
          onChange={setIncrements}
        />

        <div className="input-container">
          <p>
            Estimated rows to create: <strong>{calculateEstimatedRows()}</strong>
          </p>
        </div>

        <button type="submit">Register Office Schedule</button>
      </form>
    </div>
  );
};

export default RegisterOfficeSchedule;